/* eslint-disable react/display-name */
import React, {
  useState,
  useMemo,
  useCallback,
  useContext,
} from 'react';

import { useTable, useSortBy, usePagination, useGlobalFilter, useRowSelect } from 'react-table';
import useSWR, { useSWRConfig } from "swr";
import useSWRSubscription from "swr/subscription";
import axios from "axios";
import dayjs from 'dayjs';
import { isEmpty } from "underscore";
import CreatableSelect from 'react-select/creatable';

import { Pagination } from "../util/tables/Pagination";
import { SearchableTable } from "../util/tables/SearchableTable";
import { IndeterminateCheckbox } from "../util/tables/IndeterminateCheckbox.jsx";
import { Om7Sense as p } from "../Om7Sense";
import {
  fullDateTimeFormatNoSecs
} from '../util/dateTime';
import { TranslationContext } from "../hooks/useTranslationContext";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

// These are ysed to create labels for the email recipients, see https://react-select.com/creatable
const components = {
  DropdownIndicator: null,
};

const createOption = (label) => ({
  label,
  value: label,
});

const dateTimeFormatter = Intl.DateTimeFormat(navigator.language, fullDateTimeFormatNoSecs);

const SCHEDULE_TYPES = [ "HOURLY", "DAILY", "WEEKLY", "MONTHLY" ];

const TIME_UNITS = [ 'HOURS', 'DAYS' ];

const SWR_KEY_REPORT_TASKS = '/apiv2/scheduler/groups/email_reports';

export const ScheduledTaskCreate = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const t = useContext(TranslationContext);

  // Precalculate weekday num to weekday translation mapping
  const weekdayNumsToNames = useMemo(() => {
    const mapping = [];
    for (let dayNum = 1; dayNum <= 7; dayNum++) {
      mapping[dayNum] = t(`cron.weekday_${dayNum}`);
    }
    return mapping;
  }, [ t ]);

  const weekdayOptions = useMemo(() =>
    weekdayNumsToNames.map((dayName, dayNum) =>
      <option key={`day_${ dayNum }`} value={ dayNum }>{ dayName }</option>
    ), [ weekdayNumsToNames ]);


  const [ saveError, setSaveError ] = useState(null);
  const [ taskName, setTaskName ] = useState("");
  const [ scheduleType, setScheduleType ] = useState(SCHEDULE_TYPES[0]);
  const [ saving, setSaving ] = useState(false);
  const [ minute, setMinute ] = useState(0);
  const [ hour, setHour ] = useState(0);
  const [ dayOfWeek, setDayOfWeek ] = useState(1);
  const [ dayOfMonth, setDayOfMonth ] = useState(1);
  const [ timeUnit, setTimeUnit ] = useState(TIME_UNITS[1]);
  const [ durationDays, setDurationDays ] = useState(7);
  const [ recipients, setRecipients ] = useState([]);
  // See https://react-select.com/creatable
  const [ recipientsInput, setRecipientsInput ] = useState('');

  const taskNameError = useMemo(
      () => taskName.length > 0 ? false : t('general.error__cannot_be_empty', { field: t("scheduler.name_header") }),
      [ taskName, t ]
  );

  const buttonLabel = useMemo(
      () => {
        if (taskNameError) {
          return taskNameError;
        }
        if (recipients.length === 0) {
          return t("scheduler.add_recipients_error");
        }

        if (saveError) {
          return t("scheduler.could_not_save_button");
        }
        return t("scheduler.create_button");
      }, [ recipients, taskNameError, t, saveError ]
  )

  const durationError = useMemo(() => {
    if (durationDays < 1) {
      return t("scheduler.duration_too_small_error");
    }
  }, [ t, durationDays ]);

  const saveEnabled = useMemo(
      () => !taskNameError && !durationError && recipients.length > 0,
      [ taskNameError, recipients, durationError ]
  );

  const { mutate } = useSWRConfig()

  const doSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);

    try {
      const repeatInfo = {
        repeat: scheduleType,
        minute,
      };
      // Add only the time values needed for this kind of repetition
      if (scheduleType !== 'HOURLY') {
        repeatInfo.hour = hour;
      }
      if (scheduleType === 'WEEKLY') {
        repeatInfo.dayOfWeek = dayOfWeek;
      }
      if (scheduleType === 'MONTHLY') {
        repeatInfo.dayOfMonth = dayOfMonth;
      }
      const result = await axios.post('/apiv2/scheduler', {
        description: taskName,
        groupName: 'email_reports',
        clazz: "com.omsense.shepherd.impl.scheduler.EmailReportJob",
        repeat: repeatInfo,
        data: {
          reportId,
          reportDurationDays: durationDays,
          reportTimeFrame: timeUnit,
          emails: recipients.map((entry) => entry.value),
        }
      });
      // Force refresh if SWR is caching the task list
      await mutate(SWR_KEY_REPORT_TASKS);
      setSaveError(null);
      // Navigate to scheduler settings
      navigate("/settings/scheduler");
    } catch (e) {
      setSaveError(e)
    } finally {
      setSaving(false);
    }

  }, [ durationDays, setSaving, mutate, setSaveError, scheduleType, recipients, reportId, name, minute, hour, dayOfWeek, dayOfMonth ])


  const createTimeInput = (getState, setState, saving, max, min = 0) =>
    <input
        disabled={ saving }
        type="number"
        min={min}
        max={max}
        value={ getState }
        onChange={ (ev) => setState(ev.target.value) }/>

  const timeFormForType = useMemo(() => {
    if (scheduleType === "HOURLY") {
      return [
        <React.Fragment>{ t('cron.format_HOURLY_1') } </React.Fragment>,
        createTimeInput(minute, setMinute, saving, 59),
        <React.Fragment> { t('cron.minutes') } </React.Fragment>
      ];
    }
    if (scheduleType === "DAILY") {
      return [
        <React.Fragment>{ t('cron.format_DAILY_1') } </React.Fragment>,
        createTimeInput(hour, setHour, saving, 23),
        <React.Fragment> : </React.Fragment>,
        createTimeInput(minute, setMinute, saving, 59),
      ];
    }
    if (scheduleType === "WEEKLY") {
      return [
        <React.Fragment>{ t('cron.format_WEEKLY_1') } </React.Fragment>,
        <select
            disabled={ saving }
            value={ dayOfWeek }
            onChange={ (ev) => setDayOfWeek(ev.target.value) }
        >
          {
            weekdayOptions
          }
        </select>,
        <React.Fragment> { t('cron.format_WEEKLY_2') } </React.Fragment>,
        createTimeInput(hour, setHour, saving, 23),
        <React.Fragment> : </React.Fragment>,
        createTimeInput(minute, setMinute, saving, 59),
      ];
    }
    if (scheduleType === "MONTHLY") {
      return [
        <React.Fragment>{ t('cron.format_MONTHLY_1') } </React.Fragment>,
        createTimeInput(dayOfMonth, setDayOfMonth, saving, 31),
        <React.Fragment> { t('cron.format_MONTHLY_2') } </React.Fragment>,
        createTimeInput(hour, setHour, saving, 23),
        <React.Fragment> : </React.Fragment>,
        createTimeInput(minute, setMinute, saving, 59),
      ];
    }
  }, [ scheduleType, hour, setHour, minute, setMinute, dayOfWeek, setDayOfWeek, dayOfMonth, setDayOfMonth, saving ] );

  const handleKeyDown = (event) => {
    if (!recipients) return;
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        setRecipients((prev) => [...prev, createOption(recipientsInput)]);
        setRecipientsInput('');
        event.preventDefault();
    }
  };

  const buttonClasses = useMemo(() => {
    const classes = ['btn']
    if (saveError) {
      classes.push('btn-error');
    } else if (saveEnabled) {
      classes.push('btn-success');
    }
    return classes.join(" ");
  }, [ saveError, saveEnabled ]);

  const durationFormField = useMemo(() =>
      <div className={"form-group " + (durationError ? "has-error"
          : "has-success")}>
        <div className="col-3 col-sm-12">
          <label className="form-label" htmlFor="input-duration">{ t(
              "scheduler.duration_header") }</label>
        </div>
        <div className="col-9 col-sm-12">
          <input
              id="input-duration"
              disabled={saving}
              type="number"
              value={ durationDays }
              onChange={(ev) => setDurationDays(+ev.target.value)}
              className="form-input" />
          {durationError && <p
              className="form-input-hint">{ durationError }</p>}
        </div>
      </div>,
      [ durationDays, setDurationDays, t, timeUnit, durationError ]);

  return (
      <div className="item">
        <h1>{t("scheduler.create_report_email_task")}</h1>
        <form className="form-horizontal">
          <div className={"form-group " + (taskNameError ? "has-error"
              : "has-success")}>
            <div className="col-3 col-sm-12">
              <label className="form-label" htmlFor="input-taskname">{t(
                  "scheduler.name_header")}</label>
            </div>
            <div className="col-9 col-sm-12">
              <input
                  id="input-taskname"
                  disabled={saving}
                  type="text"
                  value={taskName}
                  onChange={(ev) => setTaskName(ev.target.value)}
                  className="form-input"
                  placeholder={t("scheduler.name_placeholder")}/>
              {taskNameError && <p
                  className="form-input-hint">{taskNameError}</p>}
            </div>
          </div>

          <div className="form-group">
            <div className="col-3 col-sm-12">
              <label className="form-label" htmlFor="input-tasktype">{t(
                  "scheduler.frequency_header")}</label>
            </div>
            <div className="col-9 col-sm-12">
              <select
                  disabled={saving}
                  value={scheduleType}
                  onChange={(ev) => setScheduleType(ev.target.value)}
                  id="input-tasktype"
              >
                {
                  SCHEDULE_TYPES.map(
                      (type) => <option key={type} value={type}>{t(
                          `cron.label_${type}`)}</option>)
                }
              </select>
            </div>
          </div>

          <div className="form-group">
            <div className="col-3 col-sm-12">
              <label className="form-label">{t("scheduler.when_header")}</label>
            </div>
            <div className="col-9 col-sm-12">
              {timeFormForType}
            </div>
          </div>

          <div className="form-group">
            <div className="col-3 col-sm-12">
              <label className="form-label" htmlFor="input-tasktype">{t(
                  "scheduler.report_time_unit_header")}</label>
            </div>
            <div className="col-9 col-sm-12">
              <select
                  disabled={saving}
                  value={timeUnit}
                  onChange={(ev) => setTimeUnit(ev.target.value)}
                  id="input-tasktype"
              >
                {
                  TIME_UNITS.map(
                      (unit) => <option key={unit} value={unit}>{t(
                          `cron.unit_${unit}`)}</option>)
                }
              </select>
            </div>
          </div>

          { durationFormField }


          <div className="form-group">
            <div className="col-3 col-sm-12">
              <label className="form-label">{t(
                  "scheduler.email_reports.email_header")}</label>
            </div>
            <div className="col-9 col-sm-12">
              <CreatableSelect
                  components={components}
                  inputValue={recipientsInput}
                  isClearable
                  isMulti
                  menuIsOpen={false}
                  onChange={(newValue) => setRecipients(newValue)}
                  onInputChange={(newValue) => setRecipientsInput(newValue)}
                  onKeyDown={handleKeyDown}
                  placeholder={t(
                      'scheduler.email_reports.add_email_placeholder')}
                  value={recipients}
              />
            </div>
          </div>
          <input className={`float-right ${buttonClasses}`}
                 disabled={!saveEnabled || saving} type="submit"
                 onClick={() => doSave()} value={buttonLabel}></input>&nbsp;
        </form>
      </div>
  );

};

const ScheduledTasksTable = ({
  columns,
  data,
  t,
  reload,
  setReload,
  setLoading,
  setError,
  mutate
}) => {
  const [reallyDelete, setReallyDelete] = useState(false);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we'll use page, which has only the rows for the active page
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    toggleRowSelected,
    setPageSize,
    selectedFlatRows,
    state,
    visibleColumns,
    setGlobalFilter,
  } = useTable({
        columns,
        data,
        initialState: {
          selectedRowIds: [],
          pageSize: 20,
          sortBy: [{id: 'name', desc: true}],
        },
        autoResetGlobalFilter: false,
        autoResetSortBy: false,
        autoResetSelectedRows: false,
      }, useGlobalFilter, useSortBy, usePagination, useRowSelect,
      hooks => {
        hooks.visibleColumns.push(columns => [
          // Let's make a column for selection
          {
            id: 'selection',
            Header: () => (
                <div>
                  {t("groups.header_remove")}
                </div>
            ),
            Cell: ({ row }) => (
                <div>
                  <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                </div>
            ),
          },
          ...columns,
        ]);
      });

  const { selectedRowIds } = state;

  const doDelete = useCallback(() => {
    if (!reallyDelete) {
      setReallyDelete(true);
    } else {
      setLoading(true);
      const jobKeysToRemove = selectedFlatRows.map(d => `${d.original.job.key.group}/${d.original.job.key.name}`);
      console.log("deleting", jobKeysToRemove);
      const deletePromises = jobKeysToRemove.map((key) => axios.delete(`/apiv2/scheduler/groups/${key}`))
      Promise.all(deletePromises)
        .then(() => mutate())
        .catch((error) => {
          setError(error)
          console.warn("Cannot delete", error);
          mutate();
      }).finally(() => setLoading(false));

    }
  }, [ setError, setReallyDelete, setLoading, selectedFlatRows, reallyDelete ]);
  const reallyRemoveButton = useMemo(() => {
      const selectedCount =  Object.keys(selectedRowIds).length;
      if (selectedCount === 0) {
        return false;
      }
      return <button className={"btn " + (reallyDelete ? "btn-error" : "btn-outline-error")} disabled={isEmpty(selectedRowIds)} onClick={doDelete}>
        {t("scheduler.button_" + (reallyDelete ? "really_remove_" : "remove_"), { count: selectedCount })}
      </button>
  }, [ selectedRowIds, reallyDelete ]);

  return (
      <React.Fragment>

        <SearchableTable tableProps={getTableProps()} globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter}
                         state={ state }  columns={columns} visibleColumns={visibleColumns} headerGroups={headerGroups}
                         prepareRow={ prepareRow } getTableBodyProps={ getTableBodyProps } page={ page } />
        <Pagination page={ page } pageCount={ pageCount } pageOptions={ pageOptions }
                    canPreviousPage={ canPreviousPage } canNextPage={ canNextPage } gotoPage={ gotoPage }
                    previousPage={ previousPage } nextPage={ nextPage } setPageSize={ setPageSize }
                    pageSize={ state.pageSize } pageIndex={ state.pageIndex }>
          { reallyRemoveButton }
        </Pagination>
      </React.Fragment>
  );
};

const SchedulerSettingsPage = ({p, t }) => {
  const [ jobs, setJobs ] = useState([]);
  const [ reload, setReload ] = useState(0);
  const [ loading, setLoading ] = useState(false);
  const [ mutateError, setMutateError ] = useState(null);

  const { data: reports, error: reportsError } = useSWRSubscription(["reports/*"], ([_], { next }) => {
    let subscription;
    p.subscribe("reports/*", () => {
      next(null, p.DS.reports);
    }, true).then((sub) => {
      subscription = sub;
    }, (error) => {
      next(error);
    });
    return () => {
      subscription?.();
    };
  });

  const { data: rawEmailTasks, error, isLoading, mutate } = useSWR(SWR_KEY_REPORT_TASKS);

  const emailTasks = useMemo(() => rawEmailTasks?.map(
      (task) => ({ nextFireDayjs: dayjs(task.triggers[0].nextFireTime), ...task })),
      [ rawEmailTasks ]
  );

  const formatCron = useCallback((cron) => {
    const RE_MATCHERS = {
      HOURLY: /0 (?<mins>\d+) \* [\?\*] \* \*/,
      DAILY: /0 (?<mins>\d+) (?<hours>\d+) [\?\*] \* \*/,
      WEEKLY: /0 (?<mins>\d+) (?<hours>\d+) \? \* (?<dayOfWeek>\d)/,
      MONTHLY: /0 (?<mins>\d+) (?<hours>\d+) (?<dayOfMonth>\d+) \* \?/
    };

    const timeFormat = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "numeric",
    });

    for (const matchType of Object.keys(RE_MATCHERS)) {
      const matcher = RE_MATCHERS[matchType];
      const results = matcher.exec(cron);
      if (results) {
        const translationKeys = {...results.groups};
        if (translationKeys.dayOfWeek) {
          translationKeys.weekDayTrans = `$t(cron.weekday_${translationKeys.dayOfWeek})`;
        }
        if (translationKeys.hours !== undefined && translationKeys.mins !== undefined) {
          // Offer an actual time, and format it according to locale
          translationKeys.time = timeFormat.format(
            new Date(2012, 11, 20, +translationKeys.hours, +translationKeys.mins, 0));
        }
        console.log('matched cron', translationKeys);
        return t(`cron.format_${matchType}`, {
          replace: translationKeys,
          interpolation: {
            skipOnVariables: false
          }
        });
      }
    }
  }, []);

  const columns = useMemo(() => [
    {
      accessor: 'job.description',
      Header: t("scheduler.description_header"),
    }, {
      accessor: row => row.job.data.emails.join(", "),
      Header: t("scheduler.email_reports.email_header"),
    }, {
      accessor: row => reports[row.job.data.reportId]?.name,
      Cell: ({ value }) => value ? value : (<b>{t("scheduler.email_reports.error_report_not_found")}</b>),
      Header: t("scheduler.email_reports.report_header"),
    }, {
      accessor: 'job.data.owner',
      Header: t("scheduler.email_reports.owner_header"),
    }, {
      accessor: 'job.data.reportDurationDays',
      Header: t("scheduler.duration_short_header"),
    }, {
      accessor: row => row.job.data.reportTimeFrame ? t(`cron.unit_${row.job.data.reportTimeFrame}`) : '',
      Header: t("scheduler.report_time_unit_short_header"),
    }, {
      accessor: row => formatCron(row.triggers[0].cron),
      Header: t("scheduler.cron_header"),
    }, {
      accessor: row => dateTimeFormatter.format(row.nextFireDayjs),
      Header: t("scheduler.next_fire_time_header"),
    }
  ], [ reports ]);

  if (isLoading || loading) {
    return  (
        <div className="loader-element">
          <div className="loading loading-lg"></div>
        </div>
    );
  }
  if (error || mutateError || reportsError) {
    return <div className="empty">
      <div className="empty-icon">
        <i className="icon icon-flag"></i>
      </div>
      <p className="empty-title h5"><strong>{ t("general.Error") }</strong></p>
      <p className="empty-subtitle">{ t("scheduler.error_loading") }</p>
    </div>;
  }
  if (isEmpty(emailTasks)) {
    return (
        <div className="empty">
          <div className="empty-icon">
            <i className="icon icon-check"></i>
          </div>
          <p className="empty-title h5">{ t("scheduler.no_tasks_header") }</p>
          <p className="empty-subtitle">{ t("scheduler.no_tasks_description") }</p>
        </div>
    );
  }

  return <div className="item">
    <div className="hero hero-sm">
      <div className="hero-body">
        <h1>{t("scheduler.title")}</h1>
        <p>{t("scheduler.subtitle")}</p>
      </div>
    </div>
    <ScheduledTasksTable t={t} p={p} data={emailTasks} columns={columns} mutate={mutate}
                      setReload={setReload} reload={reload} setLoading={setLoading} setError={setMutateError} />
  </div>;
};

export default SchedulerSettingsPage;